import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Trophy, Users, Gamepad2, BarChart3, 
  ChevronRight, ChevronLeft, X, Sparkles,
  ArrowRight
} from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";

const ONBOARDING_STEPS = [
  {
    id: 1,
    icon: Trophy,
    title: "Liglere Hoş Geldin!",
    subtitle: "Rekabeti dijitale taşı",
    description: "Arkadaşlarınla lig oluştur, haftalık maçları kaydet ve şampiyon olmak için yarış. Her hafta puanlar güncellenir, sıralama değişir.",
    color: "#C6A87C",
    tips: [
      "4 kişilik lig oluştur",
      "Haftalık sonuçları gir",
      "Otomatik puan hesaplama"
    ]
  },
  {
    id: 2,
    icon: Gamepad2,
    title: "8 Farklı Oyun",
    subtitle: "Geleneksel oyunlar, modern takip",
    description: "101 Okey, Batak, Pişti, Tavla ve daha fazlası... Her oyunun kendine özgü puanlama sistemi var. Hatta kendi oyununu bile oluşturabilirsin!",
    color: "#4DFFFF",
    tips: [
      "101 Okey için ceza takibi",
      "Batak için el sayısı",
      "Özel oyun şablonları"
    ]
  },
  {
    id: 3,
    icon: Users,
    title: "Sosyal Platform",
    subtitle: "Kahve arkadaşlarınla bağlan",
    description: "Arkadaşlarını takip et, başarılarını kutla ve mesajlaş. Diğer liglerden haberdar ol, turnuvalara katıl.",
    color: "#22C55E",
    tips: [
      "Arkadaş takip sistemi",
      "Anlık bildirimler",
      "Aktivite akışı"
    ]
  },
  {
    id: 4,
    icon: BarChart3,
    title: "Detaylı İstatistikler",
    subtitle: "Her anı analiz et",
    description: "Kişisel istatistiklerini görüntüle, rozetler kazan ve performansını takip et. AI yorumcumuz her haftanın öne çıkan anlarını özetliyor!",
    color: "#F97316",
    tips: [
      "Haftalık performans grafikleri",
      "Rozet sistemi",
      "AI maç yorumları"
    ]
  }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const completeOnboarding = async () => {
    setIsExiting(true);
    try {
      await api.completeOnboarding();
    } catch (error) {
      console.error("Onboarding completion error:", error);
    }
    setTimeout(() => {
      navigate("/dashboard");
    }, 300);
  };

  const step = ONBOARDING_STEPS[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  return (
    <div 
      className={`min-h-screen flex items-center justify-center px-4 py-8 transition-opacity duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      data-testid="onboarding-page"
    >
      <div className="w-full max-w-lg">
        {/* Skip Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleSkip}
            className="text-sm text-[#64748B] hover:text-white flex items-center gap-1 transition-colors"
            data-testid="skip-onboarding-btn"
          >
            Atla <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mb-8">
          {ONBOARDING_STEPS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? `w-8 bg-[${step.color}]` 
                  : index < currentStep
                    ? "bg-white/40"
                    : "bg-white/20"
              }`}
              style={{ backgroundColor: index === currentStep ? step.color : undefined }}
            />
          ))}
        </div>

        {/* Card */}
        <div className="glass-card p-8 text-center relative overflow-hidden">
          {/* Background Glow */}
          <div 
            className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: step.color }}
          />
          
          {/* Icon */}
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 relative"
            style={{ backgroundColor: `${step.color}20` }}
          >
            <Icon className="w-10 h-10" style={{ color: step.color }} />
            <Sparkles 
              className="w-4 h-4 absolute -top-1 -right-1"
              style={{ color: step.color }}
            />
          </div>

          {/* Content */}
          <h2 
            className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider mb-1"
            style={{ color: step.color }}
          >
            {step.title}
          </h2>
          <p className="text-sm text-[#64748B] mb-4">{step.subtitle}</p>
          <p className="text-[#B1C1C1] mb-6 leading-relaxed">
            {step.description}
          </p>

          {/* Tips */}
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <div className="space-y-2">
              {step.tips.map((tip, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-left">
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${step.color}30` }}
                  >
                    <span style={{ color: step.color }} className="text-xs font-bold">✓</span>
                  </div>
                  <span className="text-[#B1C1C1]">{tip}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`text-white/70 hover:text-white ${currentStep === 0 ? 'invisible' : ''}`}
              data-testid="prev-step-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Geri
            </Button>

            <span className="text-sm text-[#64748B]">
              {currentStep + 1} / {ONBOARDING_STEPS.length}
            </span>

            <Button
              onClick={handleNext}
              className="font-['Barlow_Condensed'] uppercase tracking-wider font-bold"
              style={{ 
                backgroundColor: step.color, 
                color: step.color === "#4DFFFF" || step.color === "#22C55E" ? "#050E2E" : "white"
              }}
              data-testid="next-step-btn"
            >
              {isLastStep ? (
                <>
                  Başla <ArrowRight className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  İleri <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="text-center text-xs text-[#64748B] mt-6">
          Bu tanıtımı istediğin zaman Ayarlar'dan tekrar görebilirsin.
        </p>
      </div>
    </div>
  );
}
